import React, { useState, useEffect, useRef } from 'react';
import { readAutosave, clearAutosave, useAutosave, useUnsavedGuard } from '../hooks/useUnsavedGuard';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft,
  Save,
  Users,
  AlertTriangle,
  Shield,
  Cloud,
  MapPin,
  Phone,
  Building2,
  Truck,
  CheckCircle,
  Clock,
  Plus,
  Trash2,
  Radio,
  HardHat,
  Shovel,
  Zap,
  FileText,
  UserCheck,
  AlertCircle,
  Thermometer,
  Eye,
  Sparkles,
  Loader2,
  Flame,
  Wrench
} from 'lucide-react';
import { api } from '@/utils/api';
// WD-T3 M4 (client): the viewer's tier/id gate which crew rows render their
// own due-equipment checklist (worker tier: ONLY the viewer's own row).
import { effectiveTier, getStoredUser } from '@/utils/roleGate';
// WD-T1: per-personnel PPE check-off + individual sign-off (spec v1.2 item 1).
// v1.5 (Brian 2026-10-03): per-ITEM check-off — line items (model + serial)
// come from GET /jha/ppe-items (the server's gear↔item resolver).
import { collectPpeSignoffIssues, PPE_ITEMS_UNAVAILABLE_BLOCKED, PPE_ITEMS_STILL_LOADING, type JhaPpeCheck, type PpeLineItem } from '@/utils/jhaPpeGear';
// Cash hardening: LOCAL calendar-day default for date inputs (the UTC-day
// idiom showed tomorrow's date to US evening users).
import { toLocalDateInputValue } from '@/utils/calendarDate';
import { JhaMemberPpeChecks } from '@/components/JhaMemberPpeChecks';
import { DueEquipmentChecklist } from '@/components/DueEquipmentChecklist';

interface Project {
  id: number;
  name: string;
  siteAddress: string;
  siteId?: string;
  latitude?: number;
  longitude?: number;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface User {
  id: number;
  firstName: string;
  lastName: string;
  phoneNumber: string | null;
  role: string;
  certifications?: string[];
}

interface TaskHazardControl {
  id: string;
  task: string;
  hazards: string;
  controls: string;
}

interface CrewMember {
  id: string;
  userId?: number | null;
  name: string;
  credentials: string[];
  signature: string;
  // WD-T1: per-person PPE check-offs ({ gearId, notUsed?, results? }).
  ppeChecks?: JhaPpeCheck[];
  // Qwen audit issue 4: accountless (visitor) signers carry contact fields;
  // account-holder rows leave them undefined and keep the picker UI.
  company?: string;
  phone?: string;
  email?: string;
}

// Standard tower industry physical hazards
const PHYSICAL_HAZARDS = [
  { id: 'slips-trips-falls', name: 'Slips, Trips, or Falls' },
  { id: 'electrical', name: 'Electrical' },
  { id: 'falling-objects', name: 'Falling Objects' },
  { id: 'struck-by', name: 'Struck By' },
  { id: 'caught-between', name: 'Caught In/Between' },
  { id: 'weather', name: 'Weather Conditions' },
  { id: 'heights', name: 'Working at Heights' },
  { id: 'rigging', name: 'Rigging/Hoisting' },
  { id: 'confined-space', name: 'Confined Space' },
  { id: 'vehicular', name: 'Vehicular Traffic' },
];

// Health hazards
const HEALTH_HAZARDS = [
  { id: 'lifting', name: 'Lifting Hazards' },
  { id: 'heat-stress', name: 'Heat Stress' },
  { id: 'cold-stress', name: 'Cold Stress' },
  { id: 'rf-eme', name: 'EME/RF Exposure' },
  { id: 'noise', name: 'Noise Exposure' },
  { id: 'dust', name: 'Dust/Particulates' },
  { id: 'chemicals', name: 'Chemical Exposure' },
  { id: 'biological', name: 'Biological Hazards' },
];

// PPE options
const PPE_OPTIONS = [
  { id: 'hard-hat', name: 'Hard Hats' },
  { id: 'safety-glasses', name: 'Safety Glasses' },
  { id: 'gloves-leather', name: 'Leather Gloves' },
  { id: 'gloves-nitrile', name: 'Nitrile Gloves' },
  { id: 'gloves-cut', name: 'Cut Resistant Gloves' },
  { id: 'steel-toe', name: 'Steel Toe Boots' },
  { id: 'high-vis', name: 'High Visibility Vest' },
  { id: 'harness', name: 'Fall Protection Harness' },
  { id: 'rf-monitor', name: 'RF Monitors' },
  { id: 'hearing', name: 'Hearing Protection' },
  { id: 'respirator', name: 'Respirator' },
  { id: 'face-shield', name: 'Face Shield' },
];

// Inspection items
const INSPECTION_OPTIONS = [
  { id: 'ppe', name: 'PPE' },
  { id: 'rescue-kit', name: 'Rescue Kit' },
  { id: 'fire-extinguisher', name: 'Fire Extinguisher' },
  { id: 'first-aid', name: 'First Aid Kit' },
  { id: 'housekeeping', name: 'Housekeeping' },
  { id: 'ground-fault', name: 'Ground Fault Protection' },
  { id: 'rigging-equipment', name: 'Rigging Equipment' },
  { id: 'fall-protection', name: 'Fall Protection Equipment' },
  { id: 'electrical-tools', name: 'Electrical Tools' },
  { id: 'vehicles', name: 'Vehicles' },
];

// Scope of work options
const SCOPE_OPTIONS = [
  'Civil',
  'Tower',
  'Electrical',
  'Antenna Installation',
  'Coax/Line Installation',
  'Maintenance',
  'Decommission',
  'Survey/Inspection',
  'Other',
];

// Credential options
const CREDENTIAL_OPTIONS = [
  'First Aid/CPR',
  'OSHA10',
  'OSHA30',
  'TS&R',
  'Rigging/Signaling',
  'Qualified Electrical Worker',
  'RF Safety',
  'NWSA TT1',
  'NWSA TT2',
  'NWSA TTT',
  'Competent Climber',
  'Competent Rescuer',
  'Authorized Climber',
  'Authorized Rescuer',
  'Crane Operator',
  'CDL',
];

// RF Control methods
const RF_CONTROL_METHODS = [
  { id: 'no-action', name: 'No Action Required' },
  { id: 'power-reduction', name: 'Power Reduction' },
  { id: 'site-shutdown', name: 'Site Shutdown' },
  { id: 'time-averaging', name: 'Time Averaging' },
  { id: 'rf-suit', name: 'RF Protective Suit' },
  { id: 'barriers', name: 'Physical Barriers' },
];

/**
 * Human-readable reason for a failed emergency-contacts lookup.
 *
 * The server returns a short, sanitized `message` for known failures
 * (400 bad input, 403 forbidden, 502 provider error, 503 not configured).
 * We surface that message so the user sees the REAL reason — the previous
 * generic "AI lookup failed" string hid it. Never show raw/technical detail.
 */
function emergencyContactsErrorMessage(error: unknown): string {
  const err = error as {
    response?: { status?: number; data?: { message?: unknown } };
  };
  const status = err?.response?.status;
  const serverMessage =
    typeof err?.response?.data?.message === 'string' ? err.response.data.message.trim() : '';

  // Prefer the server's own actionable message when it sent one.
  if (serverMessage) return serverMessage;

  if (!err?.response) {
    // fetch/axios network failure — there was no reply at all.
    return 'Could not reach the server. Please enter contacts manually.';
  }
  if (status === 400) {
    return 'Enter a valid site address to look up contacts.';
  }
  if (status === 403) {
    return 'You do not have permission to look up emergency contacts. Please enter them manually.';
  }
  if (status === 402) {
    return 'BILLING_EXPIRED';
  }
  if (status === 503) {
    return 'Emergency contacts lookup is not configured. Please enter contacts manually.';
  }
  if (status === 502) {
    return 'Emergency contacts provider error. Please enter contacts manually.';
  }
  return 'AI lookup failed. Please enter contacts manually.';
}

/**
 * WD-T1: build the prefilled form from a source JHA. Carries over the
 * static details — project, structured address + lat/lng, rescue plan and
 * looked-up emergency contacts, hazards, controls, tasks, RF safety, civil
 * work, site visitors — and defaults the crew to the source JHA's roster
 * with signatures and PPE check-offs CLEARED (each person must check off
 * and sign the new assessment themselves). Everything stays editable.
 */
function applyCopyPrefill(prev: any, src: any): any {
  const job = src.jobInfo || {};
  const em = src.emergencyInfo || {};
  const haz = src.hazards || {};
  const ctr = src.controls || {};
  const rf = src.rfSafety || {};
  const cw = src.civilWork || {};
  const copiedCrew = (Array.isArray(src.crewMembers) ? src.crewMembers : [])
    .filter((m: any) => m && typeof m === 'object')
    .map((m: any, i: number) => ({
      id: `copy-${Date.now()}-${i}`,
      userId: m.userId ?? null,
      name: m.name || '',
      credentials: Array.isArray(m.credentials) ? [...m.credentials] : [],
      signature: '',
      ppeChecks: [],
      ...(m.email !== undefined ? { email: m.email } : m.userId == null ? { email: '' } : {}),
      ...(m.company !== undefined ? { company: m.company } : {}),
      ...(m.phone !== undefined ? { phone: m.phone } : {}),
    }));
  const copiedTasks = (Array.isArray(src.tasks) && src.tasks.length > 0
    ? src.tasks
    : prev.tasks
  ).map((t: any, i: number) => ({
    id: `copy-task-${Date.now()}-${i}`,
    task: t?.task || '',
    hazards: t?.hazards || '',
    controls: t?.controls || '',
  }));
  return {
    ...prev,
    projectId: src.projectId != null ? String(src.projectId) : prev.projectId,
    date: toLocalDateInputValue(), // new day — always today
    supervisorName: job.supervisorName || '',
    siteOwner: job.siteOwner || prev.siteOwner,
    siteId: job.siteId || '',
    latitude: job.latitude != null && job.latitude !== '' ? String(job.latitude) : prev.latitude,
    longitude: job.longitude != null && job.longitude !== '' ? String(job.longitude) : prev.longitude,
    siteAddress: job.siteAddress || prev.siteAddress,
    city: job.city || '',
    state: job.state || '',
    zipCode: job.zipCode || '',
    scopeOfWork: src.scopeOfWork || prev.scopeOfWork,
    elevatedWork: !!src.elevatedWork,
    workDescription: src.workDescription || '',
    is911Functional: em.is911Functional ?? prev.is911Functional,
    emergencyRescuePlan: em.rescuePlan || prev.emergencyRescuePlan,
    hospitalName: em.hospital?.name || '',
    hospitalAddress: em.hospital?.address || '',
    hospitalPhone: em.hospital?.phone || '',
    clinicName: em.clinic?.name || '',
    clinicAddress: em.clinic?.address || '',
    clinicPhone: em.clinic?.phone || '',
    policeName: em.police?.name || '',
    policeAddress: em.police?.address || '',
    policePhone: em.police?.phone || '',
    policeNonEmergency: em.police?.nonEmergency || '',
    fireName: em.fire?.name || '',
    fireAddress: em.fire?.address || '',
    firePhone: em.fire?.phone || '',
    fireNonEmergency: em.fire?.nonEmergency || '',
    electricUtility: em.utilities?.electric?.name || '',
    electricPhone: em.utilities?.electric?.phone || '',
    gasUtility: em.utilities?.gas?.name || '',
    gasPhone: em.utilities?.gas?.phone || '',
    waterUtility: em.utilities?.water?.name || '',
    waterPhone: em.utilities?.water?.phone || '',
    evacuationPoint: em.evacuationPoint || '',
    communicationPoint: em.communicationPoint || prev.communicationPoint,
    physicalHazards: Array.isArray(haz.physical) ? [...haz.physical] : [],
    healthHazards: Array.isArray(haz.health) ? [...haz.health] : [],
    selectedPPE: Array.isArray(ctr.ppe) && ctr.ppe.length > 0 ? [...ctr.ppe] : prev.selectedPPE,
    selectedInspections: Array.isArray(ctr.inspections) && ctr.inspections.length > 0 ? [...ctr.inspections] : prev.selectedInspections,
    additionalConsiderations: ctr.additionalConsiderations || '',
    tasks: copiedTasks,
    multiEmployerAcknowledged: !!src.multiEmployerAcknowledged,
    rfSafetyPlan: rf.plan || prev.rfSafetyPlan,
    groundMonitorSN: rf.groundMonitorSN || '',
    groundMPE: rf.groundMPE != null && rf.groundMPE !== '' ? String(rf.groundMPE) : prev.groundMPE,
    structureMonitorSN: rf.structureMonitorSN || '',
    structureMPE: rf.structureMPE != null && rf.structureMPE !== '' ? String(rf.structureMPE) : prev.structureMPE,
    rfControlMethods: Array.isArray(rf.controlMethods) && rf.controlMethods.length > 0 ? [...rf.controlMethods] : [],
    electricalDeEnergized: !!cw.electricalDeEnergized,
    electricalEnergized: !!cw.electricalEnergized,
    is811Notified: cw.is811Notified || 'N/A',
    excavationType: cw.excavationType || '',
    excavationDepth: cw.excavationDepth || '',
    caveInControls: cw.caveInControls || '',
    crewMembers: copiedCrew,
    siteVisitors: Array.isArray(src.siteVisitors) ? [...src.siteVisitors] : [],
  };
}

export function CreateJHAPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // WD-T3 M4 (client): viewer identity from the repo-standard stored user —
  // a worker-tier viewer gets a due-equipment checklist ONLY on their own
  // crew row (the server returns only the caller's gear for workers anyway;
  // rendering it on other members' cards was false provenance), and the kit
  // pre-use save is foreman+ capability.
  const viewer = getStoredUser();
  const viewerIsWorker = effectiveTier(viewer.role) === 'CREW_MEMBER';
  const canRecordKitCheck = !viewerIsWorker;
  const copyFrom = searchParams.get('copyFrom');
  // WD-T1: 'Copy to create new' — prefill from a previous JHA (source is
  // only ever READ; the copied JHA is a brand-new record).
  const [copiedFromId, setCopiedFromId] = useState<number | null>(null);
  const [copyError, setCopyError] = useState('');
  // WD-T1: client-side per-person PPE/sign-off issues (why + how each).
  const [ppeIssues, setPpeIssues] = useState<string[]>([]);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectAddress, setNewProjectAddress] = useState('');
  const [newProjectCity, setNewProjectCity] = useState('');
  const [newProjectState, setNewProjectState] = useState('');
  const [newProjectZipCode, setNewProjectZipCode] = useState('');
  const [projectError, setProjectError] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [startTime] = useState(Date.now());

  // Form state
  const [formData, setFormData] = useState({
    // Job Information
    projectId: '',
    date: toLocalDateInputValue(),
    supervisorName: '',
    siteOwner: '',
    siteId: '',
    latitude: '',
    longitude: '',
    siteAddress: '',
    city: '',
    state: '',
    zipCode: '',
    
    // Work Being Performed
    scopeOfWork: 'Tower',
    elevatedWork: false,
    workDescription: '',
    
    // Emergency Procedures
    is911Functional: true,
    emergencyRescuePlan: 'CALL 911, INFORM OF RESCUE IN PROGRESS, TREAT VICTIM, PERFORM FIRST AID AND CPR IF NECESSARY, WAIT FOR EMT TO ARRIVE',
    hospitalName: '',
    hospitalAddress: '',
    hospitalPhone: '',
    clinicName: '',
    clinicAddress: '',
    clinicPhone: '',
    // Expanded emergency contacts
    policeName: '',
    policeAddress: '',
    policePhone: '',
    policeNonEmergency: '',
    fireName: '',
    fireAddress: '',
    firePhone: '',
    fireNonEmergency: '',
    // Utilities
    electricUtility: '',
    electricPhone: '',
    gasUtility: '',
    gasPhone: '',
    waterUtility: '',
    waterPhone: '',
    evacuationPoint: '',
    communicationPoint: 'TRUCK',
    
    // Weather
    weatherTemp: '',
    weatherCondition: 'Clear',
    windSpeed: '',
    
    // Job Site Exposures
    // UAT-007 (safety attestation, fail-honest): NO hazard/PPE/inspection is
    // pre-checked. Every checked item must be an affirmative user choice, so
    // a signed record can never contain invented safety assertions.
    physicalHazards: [] as string[],
    healthHazards: [] as string[],

    // Hazard Control Measures
    selectedPPE: [] as string[],
    selectedInspections: [] as string[],
    additionalConsiderations: '',
    
    // Tasks, Hazards, Controls
    tasks: [{ id: '1', task: '', hazards: '', controls: '' }] as TaskHazardControl[],
    
    // Multi-Employer Acknowledgment
    multiEmployerAcknowledged: false,
    
    // RF Safety Plan
    rfSafetyPlan: 'All personnel must have annual RF site safety awareness training. All personnel must be able to read and understand RF site signage. All personnel must be authorized to access the RF site. Maximum Permissible Exposure (MPE) levels must be determined at each work location.',
    groundMonitorSN: '',
    // UAT-007: no invented RF assertions — the MPE number and control methods
    // start empty; the crew must measure/choose them.
    groundMPE: '',
    structureMonitorSN: '',
    structureMPE: '',
    rfControlMethods: [] as string[],
    
    // Civil Work Section
    electricalDeEnergized: false,
    electricalEnergized: false,
    is811Notified: 'N/A' as 'Yes' | 'No' | 'N/A',
    excavationType: '',
    excavationDepth: '',
    caveInControls: '',
    
    // Project Personnel
    crewMembers: [] as CrewMember[],
    
    // Site Visitors
    siteVisitors: [] as string[],
  });

  // AI Assist state
  const [aiLoading, setAiLoading] = useState({
    emergencyContacts: false,
    geocode: false,
    hazards: false,
    controls: false,
  });
  // Inline notice shown next to the AI Autofill Contacts button (never a bare
  // alert): explains why the action could not run and what to do about it.
  const [contactsNotice, setContactsNotice] = useState<{ tone: 'info' | 'error'; text: string; billingLink?: true } | null>(null);
  const [geocodeNotice, setGeocodeNotice] = useState<{ tone: 'info' | 'error'; text: string } | null>(null);
  const [aiSuggestions, setAiSuggestions] = useState<{
    hazards: Array<{ id: string; name: string; category: string; reason: string }>;
  }>({ hazards: [] });

  useEffect(() => {
    loadData();
  }, []);

  // ── WD-T1: 'Copy to create new' prefill from a source JHA (read-only) ──
  useEffect(() => {
    if (copyFrom) {
      let cancelled = false;
      (async () => {
        try {
          const res = await api.get(`/jha/${copyFrom}`);
          if (cancelled) return;
          const src = res.data?.data;
          if (!src) {
            setCopyError('Could not load the JHA to copy from. Start a blank assessment or pick another one.');
            return;
          }
          setFormData((prev: typeof formData) => applyCopyPrefill(prev, src));
          setCopiedFromId(Number(copyFrom));
          setCopyError('');
        } catch (error: any) {
          if (!cancelled) {
            setCopyError(
              error?.response?.data?.message
                ? `Could not copy that JHA (${error.response.data.message}). Start a blank assessment or pick another one.`
                : 'Could not load the JHA to copy from. Start a blank assessment or pick another one.'
            );
          }
        }
      })();
      return () => { cancelled = true; };
    }
    return undefined; // no copyFrom: no prefill, no cleanup
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyFrom]);

  // ── WD-T5: "Create JHA for this job" — /jha/create?projectId=<n> prefill ──
  // The My Jobs sub-dashboard deep-links here with the job pre-selected. The
  // project must come from the caller's assignable list (GET /jha/projects is
  // already worker-scoped to ACTIVE assignments), so the prefill applies only
  // once that list has actually loaded and only if the job is in it —
  // otherwise an honest notice explains why, never a silent no-op. copyFrom
  // wins if both are present (the source JHA's own project is more specific).
  // SOL-002 fix: "loaded" is tracked explicitly — an empty list or a failed
  // load must show the notice, not wait forever for a non-empty list.
  const [projectPrefillNotice, setProjectPrefillNotice] = useState('');
  const [crewLoadNotice, setCrewLoadNotice] = useState('');
  const [projectsLoadState, setProjectsLoadState] = useState<'loading' | 'loaded' | 'failed'>('loading');
  useEffect(() => {
    if (copyFrom) return;
    const pid = searchParams.get('projectId');
    if (!pid) return;
    if (projectsLoadState === 'loading') return;
    if (projectsLoadState === 'failed') {
      setProjectPrefillNotice(
        `Could not load your assignable project list, so project #${pid} could not be pre-selected. Refresh the page and try again — if it keeps failing, ask your supervisor to check your job assignment.`
      );
      return;
    }
    const match = projects.find((p) => String(p.id) === pid);
    if (!match) {
      setProjectPrefillNotice(
        `Project #${pid} is not in your assignable project list. Pick the job from the dropdown (if you're assigned to it, it will be listed there).`
      );
      return;
    }
    handleProjectChange(pid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copyFrom, searchParams, projects, projectsLoadState]);

  // ── S1: autosave + restore — backing out of the JHA no longer loses input ──
  const JHA_DRAFT_KEY = 'conformara-jha-create-draft';
  const [draftRestored, setDraftRestored] = useState(false);
  // UAT-010 (fail honest): once a JHA is successfully submitted the draft is
  // DEAD — it must never reappear as "unsaved work" (risking an accidental
  // duplicate/edited reuse of a signed record). clearAutosave() alone is not
  // enough: navigating away re-renders this component one last time and the
  // autosave effect would write the just-submitted state right back. The
  // enabled flag closes that window.
  const [draftSuppressed, setDraftSuppressed] = useState(false);
  // Restore once on mount (before autosave is enabled so the restored value
  // doesn't immediately overwrite anything).
  useEffect(() => {
    const saved = readAutosave<{ formData: any; step: number }>(JHA_DRAFT_KEY);
    if (saved?.formData) {
      draftRestoredRef.current = true;
      setFormData((prev: typeof formData) => ({ ...prev, ...saved.formData, tasks: saved.formData.tasks?.length ? saved.formData.tasks : prev.tasks }));
      if (saved.step && saved.step >= 1 && saved.step <= totalSteps) setStep(saved.step);
      setDraftRestored(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Autosave on every change — DISABLED once a submission succeeded.
  useAutosave(JHA_DRAFT_KEY, { formData, step }, !draftSuppressed);
  // Dirty = the operator entered anything meaningful.
  const jhaDirty = !!formData.projectId || !!formData.supervisorName?.trim() || !!formData.workDescription?.trim()
    || formData.tasks.some((t: any) => t.task?.trim() || t.hazards?.trim())
    || !!formData.siteAddress?.trim() || formData.crewMembers.length > 0 || formData.siteVisitors.length > 0;
  useUnsavedGuard(jhaDirty);
  const discardJhaDraft = () => {
    clearAutosave(JHA_DRAFT_KEY);
    window.location.reload();
  };

  // LUNA-001 fix — the server's crew-list contract (jha.service.getCrewMembers):
  // worker-tier callers MUST send ?projectId= (they get a scoped 404 without
  // it) and receive only that job's assigned members. The old single
  // Promise.all without projectId failed the WHOLE load for workers — projects
  // never populated, so the deep link could not select the job — and one
  // request's failure starved the other. Projects and crew load independently
  // now, and the crew list refetches whenever the job selection changes.
  // DELTA-01 fix — request-sequence guard: a slower response for a PREVIOUS
  // job must never overwrite the crew list of the job currently selected
  // (rapid A→B switches resolve out of order all the time).
  const crewLoadSeq = useRef(0);
  // DE-004: the project's ASSIGNED workers are seeded as personnel rows for
  // confirmation (removable, signatures still required) — a foreman should not
  // have to re-select people the system already knows are on the job.
  const personnelSeededForRef = useRef<string | null>(null);
  const draftRestoredRef = useRef(false);
  const loadCrewMembers = async (projectId?: string | null) => {
    const seq = ++crewLoadSeq.current;
    try {
      const url = projectId ? `/jha/crew-members?projectId=${projectId}` : '/jha/crew-members';
      const res = await api.get(url);
      if (seq !== crewLoadSeq.current) return; // stale response for an older job — drop
      const roster = res.data.data || [];
      setUsers(roster);
      setCrewLoadNotice('');
      // DE-004: seed the project-assigned workers as personnel rows (once per
      // job) — only when the roster is still empty, a draft was NOT restored
      // (the draft owns its roster), and there is real project context.
      if (
        projectId &&
        personnelSeededForRef.current !== projectId &&
        !draftRestoredRef.current &&
        roster.length > 0
      ) {
        personnelSeededForRef.current = projectId;
        setFormData((prev: any) => {
          if (prev.crewMembers.length > 0) return prev; // user already has rows — never overwrite
          return {
            ...prev,
            crewMembers: roster
              .filter((u: any) => u && typeof u.id === 'number')
              .map((u: any, i: number) => ({
                id: `seed-${Date.now()}-${i}`,
                userId: u.id,
                name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim(),
                credentials: Array.isArray(u.certifications) ? [...u.certifications] : [],
                signature: '', // each person still signs for themselves
                ppeChecks: [],
              })),
          };
        });
      }
    } catch (error: any) {
      if (seq !== crewLoadSeq.current) return; // stale failure — the newer request owns the state
      setUsers([]);
      if (error?.response?.status === 404) {
        // Worker without a selected job gets a scoped 404 BY DESIGN — the
        // crew list loads once a job is picked. Not an error; no notice.
        setCrewLoadNotice('');
        return;
      }
      setCrewLoadNotice('Could not load the crew list. Refresh the page and try again; if it keeps failing, ask your supervisor to check your job assignment.');
    }
  };

  const loadData = async () => {
    try {
      const projectsRes = await api.get('/jha/projects');
      setProjects(projectsRes.data.data || []);
      setProjectsLoadState('loaded');
    } catch (error) {
      console.error('Error loading data:', error);
      setProjectsLoadState('failed');
    }
    await loadCrewMembers(searchParams.get('projectId'));
  };

  const createProject = async () => {
    setCreatingProject(true); setProjectError('');
    try {
      const response = await api.post('/jha/projects', {
        name: newProjectName,
        siteAddress: newProjectAddress,
        city: newProjectCity || undefined,
        state: newProjectState || undefined,
        zipCode: newProjectZipCode || undefined,
      });
      const project = response.data.data;
      setProjects(previous => [...previous, project]);
      setFormData(previous => ({ ...previous, projectId: String(project.id) }));
      setNewProjectName(''); setNewProjectAddress(''); setNewProjectCity(''); setNewProjectState(''); setNewProjectZipCode('');
    } catch (error: any) { setProjectError(error.response?.data?.message || 'Unable to create project. Ask your company administrator for access.'); }
    finally { setCreatingProject(false); }
  };

  const handleProjectChange = (projectId: string) => {
    const project = projects.find(p => p.id === parseInt(projectId));
    // LUNA-JHA-002 (delta audit 2026-10-06, HIGH): the roster is PROJECT-scoped.
    // Switching jobs must clear the personnel rows (and reset the DE-004 seed
    // marker) so the new job's assigned workers re-seed — job A's crew must
    // never survive into job B's JHA (a signed roster naming another job's
    // personnel is a wrong safety record). Re-selecting the SAME job keeps the
    // rows (signatures/check-offs are real work, not leakage).
    setFormData(prev => {
      const switched = prev.projectId !== projectId;
      if (switched) personnelSeededForRef.current = null;
      const base = project
        ? {
            siteId: project.siteId || '',
            siteAddress: project.siteAddress || '',
            city: project.city || '',
            state: project.state || '',
            zipCode: project.zipCode || '',
            latitude: project.latitude?.toString() || '',
            longitude: project.longitude?.toString() || '',
          }
        : {};
      return {
        ...prev,
        ...base,
        projectId,
        crewMembers: switched ? [] : prev.crewMembers,
      };
    });
    // LUNA-001: the crew list is JOB-scoped for workers — refetch it for the
    // newly selected job (foreman+ callers get the company directory either
    // way; the server ignores projectId for non-worker tiers).
    void loadCrewMembers(projectId);
  };

  const addTask = () => {
    setFormData(prev => ({
      ...prev,
      tasks: [...prev.tasks, { id: Date.now().toString(), task: '', hazards: '', controls: '' }],
    }));
  };

  const removeTask = (id: string) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.filter(t => t.id !== id),
    }));
  };

  const updateTask = (id: string, field: keyof TaskHazardControl, value: string) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.map(t => t.id === id ? { ...t, [field]: value } : t),
    }));
  };

  const addCrewMember = () => {
    setFormData(prev => ({
      ...prev,
      crewMembers: [...prev.crewMembers, { 
        id: Date.now().toString(), 
        name: '', 
        credentials: [], 
        signature: '' 
      }],
    }));
  };

  // Qwen audit issue 4: accountless visitor signer (no user account to pick).
  const addVisitor = () => {
    setFormData(prev => ({
      ...prev,
      crewMembers: [...prev.crewMembers, {
        id: `guest-${Date.now()}`,
        userId: null,
        name: '',
        company: '',
        phone: '',
        email: '',
        credentials: [],
        signature: '',
      }],
    }));
  };

  const isVisitorRow = (m: CrewMember) => m.userId == null && m.email !== undefined;

  const removeCrewMember = (id: string) => {
    setFormData(prev => ({
      ...prev,
      crewMembers: prev.crewMembers.filter(m => m.id !== id),
    }));
  };

  const updateCrewMember = (id: string, field: keyof CrewMember, value: any) => {
    setFormData(prev => ({
      ...prev,
      crewMembers: prev.crewMembers.map(m => m.id === id ? { ...m, [field]: value } : m),
    }));
  };

  // v1.5 (Brian 2026-10-03, per-ITEM PPE check-off): load each account
  // holder's checklist LINE ITEMS (one per physical piece — model + serial)
  // from the server's gear↔item resolver. The checklist never guesses which
  // item a check-off covers. null = not loaded yet (loading/error states are
  // shown inside the checklist); a loaded map may legitimately be empty.
  const [ppeItems, setPpeItems] = useState<Record<number, PpeLineItem[]> | null>(null);
  const [ppeItemsError, setPpeItemsError] = useState<string | null>(null);
  // Luna delta8-2: the gate must also hold while the gear-list request is
  // IN FLIGHT — a fast submit before the resolver returns is unverifiable.
  const [ppeItemsLoading, setPpeItemsLoading] = useState(false);
  const ppeUserIdsKey = Array.from(new Set((formData.crewMembers || [])
    .map((m: any) => (m && typeof m.userId === 'number' ? m.userId : null))
    .filter((v: any): v is number => typeof v === 'number')))
    .sort((a, b) => a - b)
    .join(',');
  useEffect(() => {
    const projectId = Number(formData.projectId);
    if (!projectId || ppeUserIdsKey === '') {
      setPpeItems(null);
      setPpeItemsError(null);
      return () => {};
    }
    let cancelled = false;
    setPpeItemsLoading(true);
    api.get(`/jha/ppe-items?projectId=${projectId}&userIds=${ppeUserIdsKey}`)
      .then((res: any) => {
        if (cancelled) return;
        const rows: Array<{ userId: number; items: PpeLineItem[] }> = res.data?.data || [];
        const map: Record<number, PpeLineItem[]> = {};
        for (const row of rows) map[row.userId] = row.items || [];
        setPpeItems(map);
        setPpeItemsError(null);
        setPpeItemsLoading(false);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setPpeItems(null);
        setPpeItemsError(err?.response?.data?.message || err?.message || 'The gear-list request failed.');
        setPpeItemsLoading(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.projectId, ppeUserIdsKey]);

  const toggleArrayItem = (field: string, item: string) => {
    setFormData(prev => {
      const arr = (prev as any)[field] as string[];
      return {
        ...prev,
        [field]: arr.includes(item) ? arr.filter(i => i !== item) : [...arr, item],
      };
    });
  };

  const composeAddress = (street: string, city: string, state: string, zip: string): string => {
    const strip = (s: string) => s.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
    const strippedStreet = strip(street.trim());
    // Build the full locality string from structured parts.
    const localityParts: string[] = [];
    if (city.trim()) localityParts.push(city.trim());
    const last = [state.trim(), zip.trim()].filter(Boolean).join(' ');
    if (last) localityParts.push(last);
    const fullLocality = localityParts.join(', ');
    if (!fullLocality) return street.trim();
    // If the stripped street already ends with the full locality, skip
    // appending entirely — prevents double-compose when the street field
    // already contains the complete address.
    if (strippedStreet.endsWith(strip(fullLocality))) return street.trim();
    return [street.trim(), fullLocality].join(', ');
  };

  // AI Assist Functions
  const aiLookupEmergencyContacts = async () => {
    if (!formData.siteAddress.trim()) {
      // Reachable, explained path: the button stays enabled and a click states
      // exactly what is missing instead of failing silently.
      setContactsNotice({ tone: 'error', text: 'Add the site address to enable autofill.' });
      return;
    }

    setContactsNotice(null);
    setAiLoading(prev => ({ ...prev, emergencyContacts: true }));
    try {
      // Compose the full address from structured parts for the provider call.
      // Also send structured fields as optional hints (backward compatible:
      // if any caller sends only the address string, it still works).
      const fullAddress = composeAddress(formData.siteAddress, formData.city, formData.state, formData.zipCode);
      const response = await api.post('/jha/ai/emergency-contacts', {
        address: fullAddress,
        city: formData.city || undefined,
        state: formData.state || undefined,
        zipCode: formData.zipCode || undefined,
        latitude: formData.latitude,
        longitude: formData.longitude,
      });

      if (response.data.success) {
        const contacts = response.data.data;
        setFormData(prev => ({
          ...prev,
          hospitalName: contacts.hospital?.name || prev.hospitalName,
          hospitalAddress: contacts.hospital?.address || prev.hospitalAddress,
          hospitalPhone: contacts.hospital?.phone || prev.hospitalPhone,
          clinicName: contacts.clinic?.name || prev.clinicName,
          clinicAddress: contacts.clinic?.address || prev.clinicAddress,
          clinicPhone: contacts.clinic?.phone || prev.clinicPhone,
          policeName: contacts.police?.name || prev.policeName,
          policeAddress: contacts.police?.address || prev.policeAddress,
          policePhone: contacts.police?.phone || prev.policePhone,
          policeNonEmergency: contacts.police?.nonEmergency || prev.policeNonEmergency,
          fireName: contacts.fire?.name || prev.fireName,
          fireAddress: contacts.fire?.address || prev.fireAddress,
          firePhone: contacts.fire?.phone || prev.firePhone,
          fireNonEmergency: contacts.fire?.nonEmergency || prev.fireNonEmergency,
          electricUtility: contacts.utilities?.electric?.name || prev.electricUtility,
          electricPhone: contacts.utilities?.electric?.phone || prev.electricPhone,
          gasUtility: contacts.utilities?.gas?.name || prev.gasUtility,
          gasPhone: contacts.utilities?.gas?.phone || prev.gasPhone,
          waterUtility: contacts.utilities?.water?.name || prev.waterUtility,
          waterPhone: contacts.utilities?.water?.phone || prev.waterPhone,
          // Fill lat/lng from the authoritative geocode in the response.
          latitude: contacts.coordinates?.latitude?.toString() ?? prev.latitude,
          longitude: contacts.coordinates?.longitude?.toString() ?? prev.longitude,
        }));
        // Surface a clear notice so the user always knows what happened.
        if (contacts.allLookupsEmpty) {
          setContactsNotice({ tone: 'error', text: 'No nearby emergency services were found for this address. Enter contacts manually.' });
        } else if (contacts.providerDegraded) {
          setContactsNotice({ tone: 'error', text: 'Some nearby lookups could not be completed right now — try again in a few minutes.' });
        } else {
          setContactsNotice({ tone: 'info', text: 'Contacts filled from the site address — review them.' });
        }
      }
    } catch (error) {
      console.error('AI lookup failed:', error);
      const msg = emergencyContactsErrorMessage(error);
      if (msg === 'BILLING_EXPIRED') {
        setContactsNotice({ tone: 'error', text: 'Your subscription or trial has expired. Visit Billing to reactivate your account, then try again.', billingLink: true });
      } else {
        setContactsNotice({ tone: 'error', text: msg });
      }
    } finally {
      setAiLoading(prev => ({ ...prev, emergencyContacts: false }));
    }
  };

  const aiGeocodeAddress = async () => {
    if (!formData.siteAddress.trim()) {
      setGeocodeNotice({ tone: 'error', text: 'Add the site address to enable geocoding.' });
      return;
    }
    setGeocodeNotice(null);
    setAiLoading(prev => ({ ...prev, geocode: true }));
    try {
      const fullAddress = composeAddress(formData.siteAddress, formData.city, formData.state, formData.zipCode);
      const response = await api.post('/jha/ai/geocode', {
        address: fullAddress,
        city: formData.city || undefined,
        state: formData.state || undefined,
        zipCode: formData.zipCode || undefined,
      });
      if (response.data.success && response.data.data) {
        const { latitude, longitude } = response.data.data;
        setFormData(prev => ({
          ...prev,
          latitude: latitude?.toString() ?? prev.latitude,
          longitude: longitude?.toString() ?? prev.longitude,
        }));
        setGeocodeNotice({ tone: 'info', text: 'Latitude/longitude filled from the site address.' });
      }
    } catch (error) {
      console.error('Geocode failed:', error);
      setGeocodeNotice({ tone: 'error', text: 'Could not geocode the address. Enter coordinates manually.' });
    } finally {
      setAiLoading(prev => ({ ...prev, geocode: false }));
    }
  };

  const aiSuggestHazards = async () => {
    setAiLoading(prev => ({ ...prev, hazards: true }));
    try {
      const response = await api.post('/jha/ai/suggest-hazards', {
        scopeOfWork: formData.scopeOfWork,
        elevatedWork: formData.elevatedWork,
        weather: formData.weatherTemp ? {
          temperature: formData.weatherTemp,
          condition: formData.weatherCondition,
          windSpeed: formData.windSpeed,
        } : undefined,
      });

      if (response.data.success) {
        const suggestions = response.data.data;
        setAiSuggestions(prev => ({ ...prev, hazards: suggestions }));
        
        // Auto-select suggested hazards
        const physicalIds = suggestions
          .filter((s: any) => s.category === 'physical')
          .map((s: any) => s.id);
        const healthIds = suggestions
          .filter((s: any) => s.category === 'health')
          .map((s: any) => s.id);

        setFormData(prev => ({
          ...prev,
          physicalHazards: [...new Set([...prev.physicalHazards, ...physicalIds])],
          healthHazards: [...new Set([...prev.healthHazards, ...healthIds])],
        }));
      }
    } catch (error) {
      console.error('AI hazard suggestion failed:', error);
    } finally {
      setAiLoading(prev => ({ ...prev, hazards: false }));
    }
  };

  const aiSuggestControls = async () => {
    const allHazards = [...formData.physicalHazards, ...formData.healthHazards];
    if (allHazards.length === 0) {
      alert('Please select at least one hazard first');
      return;
    }

    setAiLoading(prev => ({ ...prev, controls: true }));
    try {
      const response = await api.post('/jha/ai/suggest-controls', {
        hazardIds: allHazards,
      });

      if (response.data.success) {
        const recommendations = response.data.data;
        
        setFormData(prev => ({
          ...prev,
          selectedPPE: [...new Set([...prev.selectedPPE, ...recommendations.ppe])],
          selectedInspections: [...new Set([...prev.selectedInspections, ...recommendations.inspections])],
          additionalConsiderations: prev.additionalConsiderations 
            ? prev.additionalConsiderations + '\n\nAI Suggested Controls:\n• ' + recommendations.controls.join('\n• ')
            : 'AI Suggested Controls:\n• ' + recommendations.controls.join('\n• '),
        }));
      }
    } catch (error) {
      console.error('AI control suggestion failed:', error);
    } finally {
      setAiLoading(prev => ({ ...prev, controls: false }));
    }
  };

  const handleSubmit = async () => {
    // WD-T1: per-person PPE check-off + individual sign-off gate (client
    // side). Every listed person must complete every governed item for their
    // own gear AND sign — no blanket sign-off.
    // MED-1 (Luna delta-7): a failed gear-list load BLOCKS submit with
    // why+how — never a predictable server-side rejection. The client mirror
    // skips asset-dependent checks without the map, so proceeding here would
    // let ambiguous/partial rows reach the authoritative gate and bounce.
    if (
      (ppeItemsError || ppeItemsLoading) &&
      formData.crewMembers.some((m: any) => m && m.userId != null && !isVisitorRow(m))
    ) {
      setPpeIssues([ppeItemsLoading ? PPE_ITEMS_STILL_LOADING : PPE_ITEMS_UNAVAILABLE_BLOCKED]);
      setStep(7);
      return;
    }
    const issues = collectPpeSignoffIssues(formData.crewMembers, ppeItems ?? undefined);
    if (issues.length > 0) {
      setPpeIssues(issues);
      setStep(7);
      return;
    }
    setPpeIssues([]);
    const missingVisitor = formData.crewMembers.find(m => isVisitorRow(m) && (!m.name.trim() || !m.email!.trim()));
    if (missingVisitor) {
      alert('Each visitor needs a name and email before saving');
      return;
    }
    setSaving(true);
    try {
      const timeSpent = Math.round((Date.now() - startTime) / 1000 / 60);

      const jhaData = {
        projectId: parseInt(formData.projectId),
        date: formData.date,
        
        // Job Information
        jobInfo: {
          supervisorName: formData.supervisorName,
          siteOwner: formData.siteOwner.trim(),
          siteId: formData.siteId,
          latitude: formData.latitude,
          longitude: formData.longitude,
          siteAddress: formData.siteAddress,
          city: formData.city || undefined,
          state: formData.state || undefined,
          zipCode: formData.zipCode || undefined,
        },
        
        // Work Being Performed
        workDescription: formData.workDescription,
        scopeOfWork: formData.scopeOfWork,
        elevatedWork: formData.elevatedWork,
        
        // Emergency Procedures
        emergencyInfo: {
          is911Functional: formData.is911Functional,
          rescuePlan: formData.emergencyRescuePlan,
          hospital: {
            name: formData.hospitalName,
            address: formData.hospitalAddress,
            phone: formData.hospitalPhone,
          },
          clinic: {
            name: formData.clinicName,
            address: formData.clinicAddress,
            phone: formData.clinicPhone,
          },
          police: {
            name: formData.policeName,
            address: formData.policeAddress,
            phone: formData.policePhone,
            nonEmergency: formData.policeNonEmergency,
          },
          fire: {
            name: formData.fireName,
            address: formData.fireAddress,
            phone: formData.firePhone,
            nonEmergency: formData.fireNonEmergency,
          },
          utilities: {
            electric: { name: formData.electricUtility, phone: formData.electricPhone },
            gas: { name: formData.gasUtility, phone: formData.gasPhone },
            water: { name: formData.waterUtility, phone: formData.waterPhone },
          },
          evacuationPoint: formData.evacuationPoint,
          communicationPoint: formData.communicationPoint,
        },
        
        // Weather
        weather: {
          temperature: formData.weatherTemp,
          condition: formData.weatherCondition,
          windSpeed: formData.windSpeed,
        },
        
        // Hazards
        hazards: {
          physical: formData.physicalHazards,
          health: formData.healthHazards,
        },
        
        // Controls
        controls: {
          ppe: formData.selectedPPE,
          inspections: formData.selectedInspections,
          additionalConsiderations: formData.additionalConsiderations,
        },
        
        // Tasks
        tasks: formData.tasks.filter(t => t.task.trim()),
        
        // Multi-Employer
        multiEmployerAcknowledged: formData.multiEmployerAcknowledged,
        
        // RF Safety
        rfSafety: {
          plan: formData.rfSafetyPlan,
          groundMonitorSN: formData.groundMonitorSN,
          groundMPE: formData.groundMPE,
          structureMonitorSN: formData.structureMonitorSN,
          structureMPE: formData.structureMPE,
          controlMethods: formData.rfControlMethods,
        },
        
        // Civil Work
        civilWork: {
          electricalDeEnergized: formData.electricalDeEnergized,
          electricalEnergized: formData.electricalEnergized,
          is811Notified: formData.is811Notified,
          excavationType: formData.excavationType,
          excavationDepth: formData.excavationDepth,
          caveInControls: formData.caveInControls,
        },
        
        // Crew
        crewMembers: formData.crewMembers,
        siteVisitors: formData.siteVisitors,
        
        completionTime: timeSpent,
      };

      await api.post('/jha', jhaData);
      // UAT-010: kill the draft for good — clear it AND stop autosave so the
      // unmount-time re-render cannot write the submitted state back.
      clearAutosave(JHA_DRAFT_KEY);
      setDraftSuppressed(true);
      navigate('/jha');
    } catch (error: any) {
      console.error('Error creating JHA:', error);
      alert(error.response?.data?.message || 'Failed to create Pre-Work Hazard Assessment');
    } finally {
      setSaving(false);
    }
  };

  const timeSpent = Math.round((Date.now() - startTime) / 1000 / 60);
  const totalSteps = 7;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* S1: autosave banner — input is no longer lost when navigating away */}
      {draftRestored && (
        <div className="bg-emerald-50 border-b border-emerald-200 text-emerald-800 text-sm px-6 py-2 flex items-center justify-between gap-3">
          <span>✓ Draft restored — your unsaved JHA progress was recovered automatically.</span>
          <button onClick={discardJhaDraft} className="text-xs underline text-emerald-700 hover:text-emerald-900">Start fresh</button>
        </div>
      )}
      {/* WD-T1: copy-to-create banner + copy failure notice */}
      {copiedFromId != null && (
        <div className="bg-blue-50 border-b border-blue-200 text-blue-800 text-sm px-6 py-2 flex items-center justify-between gap-3">
          <span>
            ✓ Copied from JHA #{copiedFromId} — project, address, rescue plan, emergency contacts, hazards and controls carried over (all editable). The date was set to today, and each person still needs to check off their own PPE inspections and sign.
          </span>
          <Link to={`/jha/${copiedFromId}`} className="text-xs underline text-blue-700 hover:text-blue-900">View source JHA</Link>
        </div>
      )}
      {copyError && (
        <div role="alert" className="bg-amber-50 border-b border-amber-300 text-amber-800 text-sm px-6 py-2">
          {copyError}
        </div>
      )}
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white sticky top-0 z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <Link to="/jha" className="text-blue-200 hover:text-white">
                <ArrowLeft className="h-6 w-6" />
              </Link>
              <div>
                <h1 className="text-xl md:text-2xl font-bold">PRE-WORK HAZARD ASSESSMENT</h1>
                <p className="text-sm text-blue-200 flex items-center gap-2 mt-1">
                  <Clock className="h-4 w-4" />
                  Time: {timeSpent} min
                  {timeSpent < 5 && <span className="text-green-300 ml-2">⚡ On track!</span>}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="text-sm text-blue-200">
                Step {step} of {totalSteps}
              </div>
              <button
                onClick={handleSubmit}
                disabled={saving || !formData.projectId || formData.crewMembers.length === 0}
                className="px-6 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition flex items-center gap-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="h-5 w-5" />
                {saving ? 'Saving...' : 'Sign & Submit'}
              </button>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4 flex gap-1">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                onClick={() => setStep(i + 1)}
                className={`flex-1 h-2 rounded-full transition cursor-pointer ${
                  i + 1 <= step ? 'bg-green-400' : 'bg-blue-700'
                }`}
              />
            ))}
          </div>
          <div className="mt-2 flex justify-between text-xs text-blue-200">
            <span>Job Info</span>
            <span>Emergency</span>
            <span>Hazards</span>
            <span>Controls</span>
            <span>Tasks</span>
            <span>RF Safety</span>
            <span>Crew</span>
          </div>
        </div>
      </div>

      {/* Form Content */}
      <div className="container mx-auto px-6 py-8 max-w-5xl">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8">
          
          {/* STEP 1: Job Information */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Job Information</h2>
                  <p className="text-gray-600">Complete before work begins and review with team</p>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {projectPrefillNotice && (
                  <p className="md:col-span-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                    {projectPrefillNotice}
                  </p>
                )}
                {crewLoadNotice && (
                  <p className="md:col-span-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
                    {crewLoadNotice}
                  </p>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Date *</label>
                  <input aria-label="Date *"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Supervisor/Competent Person *</label>
                  <input aria-label="Supervisor/Competent Person *"
                    type="text"
                    value={formData.supervisorName}
                    onChange={(e) => setFormData(prev => ({ ...prev, supervisorName: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter supervisor name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Project *</label>
                  <select aria-label="Project"
                    value={formData.projectId}
                  onChange={(e) => handleProjectChange(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select project...</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.name} - {project.siteAddress}
                      </option>
                    ))}
                  </select>
                  <details className="mt-3 border rounded p-3">
                    <summary className="cursor-pointer text-blue-700">Create a project</summary>
                    <p className="text-sm my-2">Add a site to your company before completing an assessment.</p>
                    <input aria-label="New project name" placeholder="Project name" value={newProjectName} onChange={e => setNewProjectName(e.target.value)} className="border rounded p-2 mr-2 w-full mb-2" />
                    <input aria-label="New project address" placeholder="Street address" value={newProjectAddress} onChange={e => setNewProjectAddress(e.target.value)} className="border rounded p-2 w-full mb-1" />
                    <div className="flex flex-wrap gap-2 mb-2">
                      <input aria-label="New project city" placeholder="City" value={newProjectCity} onChange={e => setNewProjectCity(e.target.value)} className="border rounded p-2 flex-1 min-w-0" />
                      <select aria-label="New project state" value={newProjectState} onChange={e => setNewProjectState(e.target.value)} className="border rounded p-2 w-16 shrink-0">
                        <option value="">St</option>
                        {['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'].map(st => (
                          <option key={st} value={st}>{st}</option>
                        ))}
                      </select>
                      <input aria-label="New project ZIP" placeholder="ZIP" value={newProjectZipCode} onChange={e => setNewProjectZipCode(e.target.value)} className="border rounded p-2 w-20 shrink-0" />
                    </div>
                    <button type="button" disabled={creatingProject || !newProjectName.trim() || !newProjectAddress.trim()} onClick={createProject} className="block mt-2 p-2 rounded bg-blue-700 text-white disabled:opacity-50">{creatingProject ? 'Creating...' : 'Create and select project'}</button>
                    {projectError && <p role="alert" className="text-red-700">{projectError}</p>}
                  </details>
                </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tower / Property Owner</label>
                  <input aria-label="Tower / Property Owner"
                    type="text"
                    value={formData.siteOwner}
                    onChange={(e) => setFormData(prev => ({ ...prev, siteOwner: e.target.value }))}
                    placeholder="Who owns the tower / property?"
                    className="w-full p-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Site / Job ID</label>
                  <input aria-label="Site / Job ID"
                    type="text"
                    value={formData.siteId}
                    onChange={(e) => setFormData(prev => ({ ...prev, siteId: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., IL17518-A"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Site Latitude</label>
                  <input aria-label="Site Latitude"
                    type="text"
                    value={formData.latitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, latitude: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., 40.635688"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Site Longitude</label>
                  <input aria-label="Site Longitude"
                    type="text"
                    value={formData.longitude}
                    onChange={(e) => setFormData(prev => ({ ...prev, longitude: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="e.g., -90.159702"
                  />
                  <button
                    type="button"
                    onClick={aiGeocodeAddress}
                    disabled={aiLoading.geocode}
                    className="mt-2 px-3 py-1.5 text-xs bg-blue-100 text-blue-800 rounded hover:bg-blue-200 transition flex items-center gap-1 disabled:opacity-50"
                  >
                    {aiLoading.geocode ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <MapPin className="h-3 w-3" />
                    )}
                    Fill from address
                  </button>
                  {geocodeNotice && (
                    <p role="alert" className={`mt-1 text-xs ${geocodeNotice.tone === 'error' ? 'text-red-600' : 'text-gray-500'}`}>
                      {geocodeNotice.text}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-1 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Street address</label>
                  <input aria-label="Street address"
                    type="text"
                    value={formData.siteAddress}
                    onChange={(e) => setFormData(prev => ({ ...prev, siteAddress: e.target.value }))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="123 Main St"
                  />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-1 min-w-0">
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input aria-label="City"
                      type="text"
                      value={formData.city ?? ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      className="w-full min-w-0 px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="City"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <select aria-label="State"
                      value={formData.state ?? ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                      className="w-full min-w-0 px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                    >
                      <option value="">—</option>
                      {['AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY'].map(st => (
                        <option key={st} value={st}>{st}</option>
                      ))}
                    </select>
                  </div>
                  <div className="min-w-0">
                    <label className="block text-sm font-medium text-gray-700 mb-1">ZIP</label>
                    <input aria-label="ZIP"
                      type="text"
                      inputMode="numeric"
                      value={formData.zipCode ?? ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, zipCode: e.target.value }))}
                      className="w-full min-w-0 px-3 sm:px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="ZIP"
                    />
                  </div>
                </div>
                {!formData.state && formData.siteAddress.trim() && (
                  <p className="text-xs text-amber-600 mt-1">Adding the state helps geocode the address more accurately.</p>
                )}
              </div>

              {/* Work Being Performed */}
              <div className="border-t pt-6 mt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  Work Being Performed
                </h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Scope of Work *</label>
                  <select aria-label="Scope of Work *"
                      value={formData.scopeOfWork}
                      onChange={(e) => setFormData(prev => ({ ...prev, scopeOfWork: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {SCOPE_OPTIONS.map((scope) => (
                        <option key={scope} value={scope}>{scope}</option>
                    ))}
                  </select>
                </div>

              <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Elevated Work Being Performed?</label>
                    <div className="flex gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={formData.elevatedWork}
                          onChange={() => setFormData(prev => ({ ...prev, elevatedWork: true }))}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span>Yes</span>
                </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          checked={!formData.elevatedWork}
                          onChange={() => setFormData(prev => ({ ...prev, elevatedWork: false }))}
                          className="w-4 h-4 text-blue-600"
                        />
                        <span>No</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Work Description</label>
                <textarea aria-label="Work Description"
                  value={formData.workDescription}
                  onChange={(e) => setFormData(prev => ({ ...prev, workDescription: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    rows={3}
                    placeholder="Describe the work to be performed today..."
                />
              </div>
              </div>

              {/* Weather */}
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="flex items-start gap-3">
                  <Cloud className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-semibold text-blue-900 mb-2">Weather Conditions</h4>
                    <div className="grid md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-1">Temperature (°F)</label>
                        <input aria-label="Temperature (°F)"
                          type="number"
                          value={formData.weatherTemp}
                          onChange={(e) => setFormData(prev => ({ ...prev, weatherTemp: e.target.value }))}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg"
                          placeholder="75"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-1">Condition</label>
                        <select aria-label="Condition"
                          value={formData.weatherCondition}
                          onChange={(e) => setFormData(prev => ({ ...prev, weatherCondition: e.target.value }))}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg"
                        >
                          <option value="Clear">Clear</option>
                          <option value="Partly Cloudy">Partly Cloudy</option>
                          <option value="Cloudy">Cloudy</option>
                          <option value="Rain">Rain</option>
                          <option value="Storm">Storm</option>
                          <option value="Snow">Snow</option>
                          <option value="Fog">Fog</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-blue-900 mb-1">Wind Speed (mph)</label>
                        <input aria-label="Wind Speed (mph)"
                          type="number"
                          value={formData.windSpeed}
                          onChange={(e) => setFormData(prev => ({ ...prev, windSpeed: e.target.value }))}
                          className="w-full px-3 py-2 border border-blue-300 rounded-lg"
                          placeholder="5"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => setStep(2)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                >
                  Next: Emergency Procedures →
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Emergency Procedures */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                    <Phone className="h-6 w-6 text-red-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Emergency Procedures</h2>
                    <p className="text-gray-600">List telephone numbers and include directions to the site</p>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <button
                    onClick={aiLookupEmergencyContacts}
                    disabled={aiLoading.emergencyContacts}
                    aria-busy={aiLoading.emergencyContacts}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition flex items-center gap-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                  >
                    {aiLoading.emergencyContacts ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Sparkles className="h-5 w-5" />
                    )}
                    AI Autofill Contacts
                  </button>
                  {aiLoading.emergencyContacts ? (
                    <p role="status" className="text-xs text-gray-500">Looking up emergency contacts…</p>
                  ) : contactsNotice ? (
                    <div>
                      <p role="alert" className={contactsNotice.tone === 'error' ? 'text-xs text-red-600' : 'text-xs text-gray-500'}>{contactsNotice.text}</p>
                      {contactsNotice.billingLink && (
                        <Link to="/billing" className="text-xs text-blue-700 underline mt-1 inline-block">Go to Billing</Link>
                      )}
                    </div>
                  ) : !formData.siteAddress.trim() ? (
                    <p role="status" className="text-xs text-gray-500">Add the site address to enable autofill.</p>
                  ) : null}
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 font-semibold flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  In the event of an emergency, call 911 or the appropriate emergency number immediately.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Are 911 systems functional with cell phone use?</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.is911Functional}
                        onChange={() => setFormData(prev => ({ ...prev, is911Functional: true }))}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span>Yes</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        checked={!formData.is911Functional}
                        onChange={() => setFormData(prev => ({ ...prev, is911Functional: false }))}
                        className="w-4 h-4 text-blue-600"
                      />
                      <span>No</span>
                    </label>
                        </div>
                          </div>
                      </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Site Specific Emergency/Rescue Plan *</label>
                <textarea aria-label="Site Specific Emergency/Rescue Plan *"
                  value={formData.emergencyRescuePlan}
                  onChange={(e) => setFormData(prev => ({ ...prev, emergencyRescuePlan: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                />
                    </div>

              {/* Hospital Info */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-red-600" />
                  Local ER/Hospital
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hospital Name</label>
                    <input aria-label="Hospital Name"
                      type="text"
                      value={formData.hospitalName}
                      onChange={(e) => setFormData(prev => ({ ...prev, hospitalName: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Hospital Address</label>
                    <input aria-label="Hospital Address"
                      type="text"
                      value={formData.hospitalAddress}
                      onChange={(e) => setFormData(prev => ({ ...prev, hospitalAddress: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input aria-label="Phone Number"
                      type="tel"
                      value={formData.hospitalPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, hospitalPhone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Clinic Info */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-blue-600" />
                  Local Occupational Clinic
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Clinic Name</label>
                    <input aria-label="Clinic Name"
                      type="text"
                      value={formData.clinicName}
                      onChange={(e) => setFormData(prev => ({ ...prev, clinicName: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Clinic Address</label>
                    <input aria-label="Clinic Address"
                      type="text"
                      value={formData.clinicAddress}
                      onChange={(e) => setFormData(prev => ({ ...prev, clinicAddress: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                    <input aria-label="Phone Number"
                      type="tel"
                      value={formData.clinicPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, clinicPhone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Police */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-blue-800" />
                  Local Police Department
                </h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Department Name</label>
                    <input aria-label="Department Name"
                      type="text"
                      value={formData.policeName}
                      onChange={(e) => setFormData(prev => ({ ...prev, policeName: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                    <input aria-label="Address"
                      type="text"
                      value={formData.policeAddress}
                      onChange={(e) => setFormData(prev => ({ ...prev, policeAddress: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Emergency</label>
                    <input aria-label="Emergency"
                      type="tel"
                      value={formData.policePhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, policePhone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="911"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Non-Emergency</label>
                    <input aria-label="Non-Emergency"
                      type="tel"
                      value={formData.policeNonEmergency}
                      onChange={(e) => setFormData(prev => ({ ...prev, policeNonEmergency: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Fire Department */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Flame className="h-5 w-5 text-orange-600" />
                  Local Fire Department
                </h3>
                <div className="grid md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Department Name</label>
                    <input aria-label="Department Name"
                      type="text"
                      value={formData.fireName}
                      onChange={(e) => setFormData(prev => ({ ...prev, fireName: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                    <input aria-label="Address"
                      type="text"
                      value={formData.fireAddress}
                      onChange={(e) => setFormData(prev => ({ ...prev, fireAddress: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Emergency</label>
                    <input aria-label="Emergency"
                      type="tel"
                      value={formData.firePhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, firePhone: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="911"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Non-Emergency</label>
                    <input aria-label="Non-Emergency"
                      type="tel"
                      value={formData.fireNonEmergency}
                      onChange={(e) => setFormData(prev => ({ ...prev, fireNonEmergency: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Utilities */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-gray-600" />
                  Local Utilities
                </h3>
                <p className="text-sm text-gray-500 mb-4">Contact in case of utility line strike or emergency</p>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <label className="block text-sm font-medium text-yellow-800 mb-2">⚡ Electric Utility</label>
                    <input aria-label="⚡ Electric Utility"
                      type="text"
                      value={formData.electricUtility}
                      onChange={(e) => setFormData(prev => ({ ...prev, electricUtility: e.target.value }))}
                      className="w-full px-3 py-2 border border-yellow-300 rounded-lg mb-2"
                      placeholder="Company name"
                    />
                    <input
                      type="tel"
                      value={formData.electricPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, electricPhone: e.target.value }))}
                      className="w-full px-3 py-2 border border-yellow-300 rounded-lg"
                      placeholder="Phone"
                    />
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <label className="block text-sm font-medium text-blue-800 mb-2">🔥 Gas Utility</label>
                    <input aria-label="🔥 Gas Utility"
                      type="text"
                      value={formData.gasUtility}
                      onChange={(e) => setFormData(prev => ({ ...prev, gasUtility: e.target.value }))}
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg mb-2"
                      placeholder="Company name"
                    />
                    <input
                      type="tel"
                      value={formData.gasPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, gasPhone: e.target.value }))}
                      className="w-full px-3 py-2 border border-blue-300 rounded-lg"
                      placeholder="Phone"
                    />
                  </div>
                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-4">
                    <label className="block text-sm font-medium text-cyan-800 mb-2">💧 Water Utility</label>
                    <input aria-label="💧 Water Utility"
                      type="text"
                      value={formData.waterUtility}
                      onChange={(e) => setFormData(prev => ({ ...prev, waterUtility: e.target.value }))}
                      className="w-full px-3 py-2 border border-cyan-300 rounded-lg mb-2"
                      placeholder="Company name"
                    />
                    <input
                      type="tel"
                      value={formData.waterPhone}
                      onChange={(e) => setFormData(prev => ({ ...prev, waterPhone: e.target.value }))}
                      className="w-full px-3 py-2 border border-cyan-300 rounded-lg"
                      placeholder="Phone"
                    />
                  </div>
                </div>
                <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-amber-800 text-sm font-semibold flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Always call 811 before digging - it's the law!
                  </p>
                </div>
              </div>

              {/* Evacuation Points */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-orange-600" />
                  Evacuation/Communication Points
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Evacuation Point</label>
                    <input aria-label="Evacuation Point"
                      type="text"
                      value={formData.evacuationPoint}
                      onChange={(e) => setFormData(prev => ({ ...prev, evacuationPoint: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., North parking lot"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Communication Point</label>
                    <input aria-label="Communication Point"
                      type="text"
                      value={formData.communicationPoint}
                      onChange={(e) => setFormData(prev => ({ ...prev, communicationPoint: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., TRUCK"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between pt-6 border-t">
                <button
                  onClick={() => setStep(1)}
                  className="px-6 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-semibold"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                >
                  Next: Job Site Exposures →
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Job Site Exposures */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center">
                    <AlertTriangle className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Job Site Exposures</h2>
                    <p className="text-gray-600">Hazard Identification: Items checked relate to existing conditions or may be a result of site operations</p>
                  </div>
                </div>
                <button
                  onClick={aiSuggestHazards}
                  disabled={aiLoading.hazards}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition flex items-center gap-2 font-semibold disabled:opacity-50 shadow-lg"
                >
                  {aiLoading.hazards ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Sparkles className="h-5 w-5" />
                  )}
                  AI Suggest Hazards
                </button>
              </div>

              {/* AI Suggestions Banner */}
              {aiSuggestions.hazards.length > 0 && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                  <p className="text-purple-800 font-semibold flex items-center gap-2 mb-2">
                    <Sparkles className="h-5 w-5" />
                    AI Suggested {aiSuggestions.hazards.length} hazards based on {formData.scopeOfWork} work
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {aiSuggestions.hazards.map(h => (
                      <span key={h.id} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs" title={h.reason}>
                        {h.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Physical Hazards */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Zap className="h-5 w-5 text-yellow-600" />
                  Physical Hazards
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {PHYSICAL_HAZARDS.map((hazard) => (
                    <label
                    key={hazard.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition ${
                        formData.physicalHazards.includes(hazard.id)
                          ? 'border-orange-500 bg-orange-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                      <input
                        type="checkbox"
                        checked={formData.physicalHazards.includes(hazard.id)}
                        onChange={() => toggleArrayItem('physicalHazards', hazard.id)}
                        className="w-4 h-4 text-orange-600 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">{hazard.name}</span>
                    </label>
                  ))}
                      </div>
                      </div>

              {/* Health Hazards */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Thermometer className="h-5 w-5 text-red-600" />
                  Health Hazards
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {HEALTH_HAZARDS.map((hazard) => (
                    <label
                      key={hazard.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition ${
                        formData.healthHazards.includes(hazard.id)
                          ? 'border-red-500 bg-red-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.healthHazards.includes(hazard.id)}
                        onChange={() => toggleArrayItem('healthHazards', hazard.id)}
                        className="w-4 h-4 text-red-600 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">{hazard.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm flex items-center gap-2">
                  <AlertCircle className="h-5 w-5" />
                  Contact your Safety Manager/Tech prior to entering any Confined Space
                </p>
              </div>

              <div className="flex justify-between pt-6 border-t">
                <button
                  onClick={() => setStep(2)}
                  className="px-6 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-semibold"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setStep(4)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                >
                  Next: Hazard Control Measures →
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: Hazard Control Measures */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Shield className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Hazard Control Measures</h2>
                    <p className="text-gray-600">Personal Protective Equipment and Monitoring Equipment</p>
                  </div>
                </div>
                <button
                  onClick={aiSuggestControls}
                  disabled={aiLoading.controls || (formData.physicalHazards.length === 0 && formData.healthHazards.length === 0)}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition flex items-center gap-2 font-semibold disabled:opacity-50 shadow-lg"
                >
                  {aiLoading.controls ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Sparkles className="h-5 w-5" />
                  )}
                  AI Suggest Controls
                </button>
              </div>

              {/* PPE */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <HardHat className="h-5 w-5 text-blue-600" />
                  PPE Required
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {PPE_OPTIONS.map((ppe) => (
                    <label
                      key={ppe.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition ${
                        formData.selectedPPE.includes(ppe.id)
                          ? 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedPPE.includes(ppe.id)}
                        onChange={() => toggleArrayItem('selectedPPE', ppe.id)}
                        className="w-4 h-4 text-green-600 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">{ppe.name}</span>
                </label>
                  ))}
                </div>
              </div>

              {/* Inspections */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Eye className="h-5 w-5 text-purple-600" />
                  Inspections
                </h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {INSPECTION_OPTIONS.map((item) => (
                    <label
                      key={item.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition ${
                        formData.selectedInspections.includes(item.id)
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.selectedInspections.includes(item.id)}
                        onChange={() => toggleArrayItem('selectedInspections', item.id)}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">{item.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Additional Considerations */}
              <div className="border-t pt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Considerations</label>
                <textarea aria-label="Additional Considerations"
                  value={formData.additionalConsiderations}
                  onChange={(e) => setFormData(prev => ({ ...prev, additionalConsiderations: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Any additional safety considerations..."
                />
              </div>

              <div className="flex justify-between pt-6 border-t">
                <button
                  onClick={() => setStep(3)}
                  className="px-6 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-semibold"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setStep(5)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                >
                  Next: Tasks, Hazards & Controls →
                </button>
              </div>
            </div>
          )}

          {/* STEP 5: Tasks, Hazards and Controls */}
          {step === 5 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Tasks, Hazards and Controls</h2>
                  <p className="text-gray-600">Add each task with corresponding hazards and control measures</p>
                </div>
              </div>

              <div className="space-y-4">
                {formData.tasks.map((task, index) => (
                  <div key={task.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900">Task #{index + 1}</h4>
                      {formData.tasks.length > 1 && (
                        <button
                          onClick={() => removeTask(task.id)}
                          className="text-red-600 hover:text-red-800 p-1"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Task Description *</label>
                        <input aria-label="Task Description *"
                          type="text"
                          value={task.task}
                          onChange={(e) => updateTask(task.id, 'task', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          placeholder="e.g., Install antenna at 150ft"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hazards</label>
                        <input aria-label="Hazards"
                          type="text"
                          value={task.hazards}
                          onChange={(e) => updateTask(task.id, 'hazards', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          placeholder="e.g., Falls, dropping tools, RF exposure"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hazard Controls</label>
                        <input aria-label="Hazard Controls"
                          type="text"
                          value={task.controls}
                          onChange={(e) => updateTask(task.id, 'controls', e.target.value)}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          placeholder="e.g., 100% tie-off, tool lanyards, RF monitoring"
                        />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

              <button
                onClick={addTask}
                className="flex items-center gap-2 px-4 py-2 text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition"
              >
                <Plus className="h-5 w-5" />
                Add Another Task
              </button>

              {/* Multi-Employer Acknowledgment */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Multi-Employer Work Sites</h3>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <ul className="text-sm text-yellow-900 space-y-1 mb-4">
                    <li>• Notify your Health and Safety Manager/Tech before working around other contractors, building occupants, or members of the public</li>
                    <li>• Do not expose other contractors, building occupants, or pedestrians to hazards</li>
                    <li>• Hazards created by other contractors must be identified and controlled</li>
                    <li>• Working around any non-company employees should be avoided</li>
                    <li>• Meet with other on-site employers to review hazards</li>
                  </ul>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.multiEmployerAcknowledged}
                      onChange={(e) => setFormData(prev => ({ ...prev, multiEmployerAcknowledged: e.target.checked }))}
                      className="w-5 h-5 text-blue-600 rounded"
                    />
                    <span className="font-medium text-yellow-900">I acknowledge and understand the above requirements</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-between pt-6 border-t">
                <button
                  onClick={() => setStep(4)}
                  className="px-6 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-semibold"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setStep(6)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                >
                  Next: RF Safety & Civil Work →
                </button>
              </div>
            </div>
          )}

          {/* STEP 6: RF Safety Plan & Civil Work */}
          {step === 6 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <Radio className="h-6 w-6 text-purple-600" />
                </div>
              <div>
                  <h2 className="text-2xl font-bold text-gray-900">RF Safety Plan</h2>
                  <p className="text-gray-600">Personnel entering a Controlled RF Environment must adhere to FCC OET Bulletin 65</p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">RF Safety Plan</label>
                <textarea aria-label="RF Safety Plan"
                  value={formData.rfSafetyPlan}
                  onChange={(e) => setFormData(prev => ({ ...prev, rfSafetyPlan: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={4}
                />
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm font-semibold">
                  If MPE readings are 100% or above, all personnel must remove themselves from the RF field and contact their safety representative for further guidance.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Ground Level Monitoring</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ground Monitor S/N</label>
                    <input aria-label="Ground Monitor S/N"
                      type="text"
                      value={formData.groundMonitorSN}
                      onChange={(e) => setFormData(prev => ({ ...prev, groundMonitorSN: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="e.g., 0047708"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Ground MPE %</label>
                    <input aria-label="Ground MPE %"
                      type="number"
                      value={formData.groundMPE}
                      onChange={(e) => setFormData(prev => ({ ...prev, groundMPE: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="0"
                    />
                  </div>
                </div>
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-900">Structure Level Monitoring</h4>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Structure Monitor S/N</label>
                    <input aria-label="Structure Monitor S/N"
                      type="text"
                      value={formData.structureMonitorSN}
                      onChange={(e) => setFormData(prev => ({ ...prev, structureMonitorSN: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Structure MPE %</label>
                    <input aria-label="Structure MPE %"
                      type="number"
                      value={formData.structureMPE}
                      onChange={(e) => setFormData(prev => ({ ...prev, structureMPE: e.target.value }))}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Control Methods for Limiting RF Exposure</label>
                <div className="grid md:grid-cols-3 gap-3">
                  {RF_CONTROL_METHODS.map((method) => (
                    <label
                      key={method.id}
                      className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition ${
                        formData.rfControlMethods.includes(method.id)
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.rfControlMethods.includes(method.id)}
                        onChange={() => toggleArrayItem('rfControlMethods', method.id)}
                        className="w-4 h-4 text-purple-600 rounded"
                      />
                      <span className="text-sm font-medium text-gray-700">{method.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Civil Work Section */}
              {(formData.scopeOfWork === 'Civil' || formData.scopeOfWork === 'Electrical') && (
                <div className="border-t pt-6">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                      <Shovel className="h-5 w-5 text-amber-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">Complete for Civil Work</h3>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                        <Zap className="h-5 w-5 text-yellow-600" />
                        Electrical
                </h4>
                      <div className="flex gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.electricalDeEnergized}
                            onChange={(e) => setFormData(prev => ({ ...prev, electricalDeEnergized: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span>De-Energized</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.electricalEnergized}
                            onChange={(e) => setFormData(prev => ({ ...prev, electricalEnergized: e.target.checked }))}
                            className="w-4 h-4 text-blue-600 rounded"
                          />
                          <span>Energized</span>
                        </label>
                    </div>
                  </div>

                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <p className="text-amber-800 text-sm font-semibold mb-2">Excavations</p>
                      <p className="text-amber-700 text-sm">
                        Notify 811 and ensure underground utilities are located prior to digging. Third party/private ground locate providers may be required.
                      </p>
                      </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">811 Notified?</label>
                        <select aria-label="811 Notified?"
                          value={formData.is811Notified}
                          onChange={(e) => setFormData(prev => ({ ...prev, is811Notified: e.target.value as 'Yes' | 'No' | 'N/A' }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="N/A">N/A</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Excavation Type & Depth</label>
                        <input aria-label="Excavation Type & Depth"
                          type="text"
                          value={formData.excavationType}
                          onChange={(e) => setFormData(prev => ({ ...prev, excavationType: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Type and depth of excavation"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Cave-In Control Measures (if excavation {'>'} 5 feet)</label>
                      <textarea
                        value={formData.caveInControls}
                        onChange={(e) => setFormData(prev => ({ ...prev, caveInControls: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={2}
                        placeholder="Describe cave-in control measures..."
                      />
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <p className="text-yellow-800 text-sm">
                        Notify your Health and Safety Manager/Tech before entering a confined space or a trench/excavation that is greater than 5' deep
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between pt-6 border-t">
                <button
                  onClick={() => setStep(5)}
                  className="px-6 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-semibold"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setStep(7)}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-semibold"
                >
                  Next: Project Personnel →
                </button>
                    </div>
            </div>
          )}

          {/* STEP 7: Project Personnel */}
          {step === 7 && (
            <div className="space-y-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                  <Users className="h-6 w-6 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Project Personnel</h2>
                  <p className="text-gray-600">Add personnel working on site with their credentials and signatures</p>
                </div>
              </div>

              {formData.crewMembers.length === 0 && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                  <p className="text-yellow-800">No crew members added yet. Add at least one crew member to continue.</p>
                </div>
              )}

              {/* WD-T1: why+how issues blocking submission */}
              {ppeIssues.length > 0 && (
                <div role="alert" className="bg-red-50 border border-red-300 rounded-lg p-4" data-testid="ppe-issues">
                  <p className="text-red-800 font-semibold mb-2 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5" />
                    Cannot submit yet — {ppeIssues.length} item{ppeIssues.length !== 1 ? 's' : ''} incomplete:
                  </p>
                  <ul className="text-sm text-red-700 list-disc list-inside space-y-1">
                    {ppeIssues.map((issue, i) => <li key={i}>{issue}</li>)}
                  </ul>
                </div>
              )}

              <div className="space-y-4">
                {formData.crewMembers.map((member, index) => (
                  <div key={member.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                        <UserCheck className="h-5 w-5 text-indigo-600" />
                        Crew Member #{index + 1}{isVisitorRow(member) && ' (visitor)'}
                      </h4>
                      <button
                        onClick={() => removeCrewMember(member.id)}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                    
                    <div className="space-y-4">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Name *</label>
                          {!isVisitorRow(member) && <select aria-label="Name *"
                            value={member.userId || ''}
                            onChange={(e) => {
                              const user = users.find(u => u.id === parseInt(e.target.value));
                              if (user) {
                                updateCrewMember(member.id, 'userId', user.id);
                                updateCrewMember(member.id, 'name', `${user.firstName} ${user.lastName}`);
                              }
                            }}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                          >
                            <option value="">Select crew member or enter manually...</option>
                            {users.map((user) => (
                              <option key={user.id} value={user.id}>
                                {user.firstName} {user.lastName}
                              </option>
                            ))}
                          </select>}
                          <input
                            type="text"
                            value={member.name}
                            onChange={(e) => updateCrewMember(member.id, 'name', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white mt-2"
                            placeholder={isVisitorRow(member) ? 'Visitor name (required)' : 'Or enter name manually'}
                          />
                          {isVisitorRow(member) && (
                            <div className="grid md:grid-cols-3 gap-4 mt-2">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                                <input
                                  type="email"
                                  value={member.email || ''}
                                  onChange={(e) => updateCrewMember(member.id, 'email', e.target.value)}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Company</label>
                                <input
                                  type="text"
                                  value={member.company || ''}
                                  onChange={(e) => updateCrewMember(member.id, 'company', e.target.value)}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                                <input
                                  type="tel"
                                  value={member.phone || ''}
                                  onChange={(e) => updateCrewMember(member.id, 'phone', e.target.value)}
                                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                                />
                              </div>
                            </div>
                          )}
                </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Signature *</label>
                          <input aria-label="Signature *"
                            type="text"
                            value={member.signature}
                            onChange={(e) => updateCrewMember(member.id, 'signature', e.target.value)}
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white font-signature text-xl"
                            placeholder="/ Initials"
                          />
                        </div>
                      </div>
                      
                      {/* WD-T1: per-person PPE check-off — account holders only
                          (visitors hold no tracked company gear and keep the
                          visitor sign flow). */}
                      {!isVisitorRow(member) && (
                        <JhaMemberPpeChecks
                          member={member}
                          items={ppeItems && member.userId != null ? ppeItems[member.userId] ?? [] : undefined}
                          itemsError={ppeItemsError}
                          onChange={(ppeChecks) => updateCrewMember(member.id, 'ppeChecks', ppeChecks)}
                        />
                      )}

                      {/* WD-T2 (C): due-today equipment surfacing — account-linked
                          crew only (visitors hold no company gear). Tier-1 gear
                          due for inspection with one-tap entry; the Tier-2 kit is
                          ONE pre-use record. No jhaId yet — the JHA is created on
                          submit, so the one-tap records carry no back-link here. */}
                      {!isVisitorRow(member) && member.userId != null &&
                        (!viewerIsWorker || member.userId === viewer.id) && (
                        <DueEquipmentChecklist
                          userId={member.userId}
                          crewLabel={member.name}
                          canRecordKitCheck={canRecordKitCheck}
                        />
                      )}

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Credentials</label>
                        <div className="flex flex-wrap gap-2">
                          {CREDENTIAL_OPTIONS.map((cred) => (
                            <label
                              key={cred}
                              className={`px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition ${
                                member.credentials.includes(cred)
                                  ? 'bg-indigo-600 text-white'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={member.credentials.includes(cred)}
                                onChange={() => {
                                  const newCreds = member.credentials.includes(cred)
                                    ? member.credentials.filter(c => c !== cred)
                                    : [...member.credentials, cred];
                                  updateCrewMember(member.id, 'credentials', newCreds);
                                }}
                                className="sr-only"
                              />
                              {cred}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={addCrewMember}
                className="flex items-center gap-2 px-4 py-2 text-indigo-600 border border-indigo-300 rounded-lg hover:bg-indigo-50 transition"
              >
                <Plus className="h-5 w-5" />
                Add Crew Member
              </button>

              <button
                type="button"
                onClick={addVisitor}
                className="flex items-center gap-2 px-4 py-2 ml-2 text-indigo-600 border border-indigo-300 rounded-lg hover:bg-indigo-50 transition"
              >
                <Plus className="h-5 w-5" />
                Add visitor (no account)
              </button>

              {/* Site Visitors */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Site Visitors</h3>
                <p className="text-sm text-gray-600 mb-4">Add any site visitors that will be present</p>
                <textarea
                  value={formData.siteVisitors.join('\n')}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    siteVisitors: e.target.value.split('\n').filter(v => v.trim()) 
                  }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={3}
                  placeholder="Enter visitor names, one per line..."
                />
              </div>

              <div className="flex justify-between pt-6 border-t">
                <button
                  onClick={() => setStep(6)}
                  className="px-6 py-3 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-semibold"
                >
                  ← Back
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving || formData.crewMembers.length === 0 || !formData.projectId}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition font-semibold flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle className="h-5 w-5" />
                  {saving ? 'Signing & Submitting...' : 'Sign & Submit Assessment'}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Time Tracker Footer */}
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white rounded-lg px-4 py-2 shadow-sm border border-gray-200">
            <Clock className="h-5 w-5 text-blue-600" />
            <span className="text-sm text-gray-600">
              Total time: <span className="font-bold text-gray-900">{timeSpent} minute{timeSpent !== 1 ? 's' : ''}</span>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
